import React from "react";
import { Row, Col } from "reactstrap";
import RequestEngine from "../../core/RequestEngine";
import CoreEngine from "../../core/CoreEngine";
import LocationPicker from "../../components/Map/LocationPicker";
import {
  FormSection,
  FormGrid,
  Field,
  TextField,
  SelectField,
  SwitchField,
  PasswordField,
  passwordIsValid,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";
import strings from "../../core/translate";
import Constants from "../../core/Constants";

// Everything a garage needs in one form: details, location on the map and photos.
// Used both by the dialog on the garages list and by the add/edit route.
export default class GarageForm extends CoreEngine {
  constructor(props) {
    super(props);
    const garage = props.garage || {};
    this.state = {
      id: garage.id || "",
      version: garage.version,
      name: garage.name || "",
      address: garage.address || "",
      latitude: garage.latitude !== undefined ? garage.latitude : "",
      longitude: garage.longitude !== undefined ? garage.longitude : "",
      phoneNumber: garage.phoneNumber || "",
      makesIds:
        garage.vehicleMake && Array.isArray(garage.vehicleMake)
          ? garage.vehicleMake.map((make) => make.id)
          : [],
      isTopRated: !!garage.isTopRated,
      topRatedRank:
        garage.topRatedRank === null || garage.topRatedRank === undefined
          ? ""
          : garage.topRatedRank,
      makes: [],
      photos: [],
      pendingPhotos: [],
      photosLoading: false,
      uploadingPhoto: false,
      loading: false,
      error: "",
      errors: {},
      // the login that belongs to this garage
      account: null,
      accountEmail: "",
      accountPassword: "",
    };
    this.engine = new RequestEngine();
    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.fetchmakes();
    if (this.state.id) {
      this.fetchPhotos(this.state.id);
      this.fetchAccount(this.state.id);
    }
  }

  async fetchAccount(branchId) {
    try {
      const response = await this.engine.getItem(
        `merchants-branches/${branchId}/account`
      );
      const account = response && response.data;
      if (account && account.id) {
        this.setState({
          account,
          accountEmail: account.user ? account.user.email : "",
        });
      }
    } catch (err) {
      // a garage without a login yet is the normal case
    }
  }

  componentWillUnmount() {
    this.state.pendingPhotos.forEach((photo) =>
      URL.revokeObjectURL(photo.preview)
    );
  }

  /**
   * A photo save cascades to its branch and bumps the branch version, so the
   * copy held by this form goes stale and the next update is refused with
   * "The field `version` does not match".
   */
  async refreshVersion(branchId) {
    try {
      const response = await this.engine.getItem(
        `merchants/${this.props.merchantId}/branches/${branchId}`
      );
      const version = response && response.data && response.data.version;
      if (version !== undefined && version !== null) {
        this.setState({ version });
      }
    } catch (err) {
      // keeping the old version only risks the conflict we already had
    }
  }

  async fetchPhotos(branchId) {
    this.setState({ photosLoading: true });
    try {
      const response = await this.engine.getItem(
        `merchant-branch/${branchId}/photos`
      );
      if (response && response.data) {
        this.setState({ photos: response.data.items || [] });
      }
    } catch (err) {
      // an empty gallery is preferable to blocking the form
    }
    this.setState({ photosLoading: false });
  }

  handleSelectPhotos = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    const { id } = this.state;
    if (id) {
      this.uploadPhotos(id, files);
      return;
    }
    // no garage yet: keep the files until it is created
    this.setState((prev) => ({
      pendingPhotos: [
        ...prev.pendingPhotos,
        ...files.map((file) => ({ file, preview: URL.createObjectURL(file) })),
      ],
    }));
  };

  async uploadPhotos(branchId, files) {
    this.setState({ uploadingPhoto: true });
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await this.engine.postItemMultipart(
          `merchant-branch/${branchId}/photos`,
          formData
        );
      } catch (err) {
        this.setState({
          error: `${strings.photos}: ${
            (err.response && err.response.data && err.response.data.message) ||
            err.message
          }`,
        });
      }
    }
    this.setState({ uploadingPhoto: false });
    await this.fetchPhotos(branchId);
    await this.refreshVersion(branchId);
  }

  handleDeletePhoto = async (photoId) => {
    const { id } = this.state;
    if (!window.confirm(strings.confirmdelete)) return;
    try {
      await this.engine.deleteItem(`merchant-branch/${id}/photos`, photoId);
      this.setState((prev) => ({
        photos: prev.photos.filter((photo) => photo.id !== photoId),
      }));
      await this.refreshVersion(id);
    } catch (err) {
      this.setState({ error: strings.error });
    }
  };

  handleRemovePending = (index) => {
    this.setState((prev) => {
      const pendingPhotos = [...prev.pendingPhotos];
      URL.revokeObjectURL(pendingPhotos[index].preview);
      pendingPhotos.splice(index, 1);
      return { pendingPhotos };
    });
  };

  handleLocationChange = ({ latitude, longitude }) => {
    this.setState({ latitude, longitude });
  };

  // every pick on the map rewrites the address: choosing a new point and
  // keeping the old address is never what was meant
  handleAddressResolved = (address) => {
    if (address && address !== this.state.address) {
      this.setState({ address, errors: { ...this.state.errors, address: "" } });
    }
  };

  validate() {
    const { id, name, address, latitude, longitude, makesIds, phoneNumber } =
      this.state;
    const trimmedAddress = `${address}`.trim();
    const trimmedName = `${name}`.trim();
    const errors = {};

    if (trimmedName && trimmedName.length < 3) {
      errors.name = strings.name_min3_hint;
    }
    // the api asks for at least 10 characters on create, 3 on update
    if (!trimmedAddress || trimmedAddress.length < (id ? 3 : 10)) {
      errors.address = strings.garage_address_hint;
    }
    if (latitude === "" || longitude === "") {
      errors.location = strings.garage_location_hint;
    }
    if (!makesIds || makesIds.length === 0) {
      errors.makesIds = `${strings.select} ${strings.vehicle_makes}`;
    }

    if (phoneNumber && !/^[0-9+()\s-]{6,20}$/.test(`${phoneNumber}`.trim())) {
      errors.phoneNumber = strings.phone_hint;
    }

    const { account, accountEmail, accountPassword } = this.state;
    const email = `${accountEmail}`.trim();

    // a new garage always gets its login, an existing one may add it later
    if (!id || email || accountPassword) {
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        errors.accountEmail = strings.email_hint;
      }
      // the api rejects anything weaker, so say so before saving
      const passwordRequired = !account;
      if (passwordRequired || accountPassword) {
        if (!passwordIsValid(accountPassword)) {
          errors.accountPassword = strings.password_hint;
        }
      }
    }

    return errors;
  }

  // creates the garage login, or updates the email / password of an existing one
  async saveAccount(branchId) {
    const { account, accountEmail, accountPassword, name } = this.state;
    const email = `${accountEmail}`.trim();

    if (!email && !accountPassword) {
      return;
    }

    const pathname = `merchants-branches/${branchId}/account`;

    if (account) {
      await this.engine.putPath(pathname, {
        ...(email && email !== (account.user && account.user.email) && { email }),
        ...(accountPassword && { password: accountPassword }),
      });
      return;
    }

    // no roleId: the api gives every garage login the garage-owner role
    await this.engine.saveItem(pathname, {
      email,
      password: accountPassword,
      firstName: `${name}`.trim() || "Garage",
      lastName: "Account",
    });
  }

  handleValidSubmit = async (event) => {
    if (event) event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ loading: true, error: "", errors: {} });

    const {
      id,
      version,
      name,
      address,
      latitude,
      longitude,
      phoneNumber,
      makesIds,
      isTopRated,
      topRatedRank,
      pendingPhotos,
    } = this.state;

    const payload = {
      name: `${name}`.trim() || undefined,
      address: `${address}`.trim(),
      latitude: Number(latitude),
      longitude: Number(longitude),
      phoneNumber: `${phoneNumber}`.trim() || undefined,
      vehiclesMake: makesIds,
      isTopRated: !!isTopRated,
      // Blank (or an unflagged garage) means "no manual rank".
      topRatedRank:
        isTopRated && `${topRatedRank}`.trim() !== ""
          ? Number(topRatedRank)
          : null,
    };

    const pathname = `merchants/${this.props.merchantId}/branches`;

    try {
      let response;
      if (id) {
        response = await this.engine.updateItem(pathname, id, {
          ...payload,
          version,
        });
      } else {
        response = await this.engine.saveItem(pathname, payload);
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const saved = response.data || {};
        const branchId = id || saved.id;

        if (!id && branchId && pendingPhotos.length > 0) {
          await this.uploadPhotos(
            branchId,
            pendingPhotos.map((photo) => photo.file)
          );
          pendingPhotos.forEach((photo) => URL.revokeObjectURL(photo.preview));
          this.setState({ pendingPhotos: [] });
        }

        if (branchId) {
          await this.saveAccount(branchId);
        }

        this.setState({ loading: false });
        this.props.onSaved(saved);
        return;
      }
    } catch (err) {
      const status = err.response && err.response.status;
      // the garage moved on since this dialog was opened: reload the version so
      // pressing save once more applies the same edits
      if (status === 409 && id) {
        await this.refreshVersion(id);
        this.setState({ error: strings.version_conflict_hint, loading: false });
        return;
      }
      const message = err.response && err.response.data && err.response.data.message;
      this.setState({
        error: Array.isArray(message)
          ? message.join(", ")
          : message || err.message || strings.error,
      });
    }

    this.setState({ loading: false });
  };

  renderPhotos() {
    const { photos, pendingPhotos, photosLoading, uploadingPhoto } = this.state;

    return (
      <div className="garage-form__photos">
        {photosLoading ? (
          <p>{strings.loading}</p>
        ) : (
          <div className="garage-form__gallery">
            {photos.map((photo) => (
              <div className="garage-form__thumb" key={photo.id}>
                <img
                  src={`${Constants.serverlink}${photo.url}`}
                  alt={strings.garage}
                />
                <button
                  type="button"
                  onClick={() => this.handleDeletePhoto(photo.id)}
                >
                  &times;
                </button>
              </div>
            ))}

            {pendingPhotos.map((photo, index) => (
              <div className="garage-form__thumb" key={photo.preview}>
                <img src={photo.preview} alt={strings.garage} />
                <button
                  type="button"
                  onClick={() => this.handleRemovePending(index)}
                >
                  &times;
                </button>
              </div>
            ))}

            <button
              type="button"
              className="garage-form__add-photo"
              disabled={uploadingPhoto}
              onClick={() =>
                this.fileInputRef.current && this.fileInputRef.current.click()
              }
            >
              {uploadingPhoto ? "..." : "+"}
            </button>
          </div>
        )}

        <input
          ref={this.fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={this.handleSelectPhotos}
        />
      </div>
    );
  }

  render() {
    const {
      loading,
      error,
      errors,
      id,
      name,
      address,
      latitude,
      longitude,
      phoneNumber,
      makes,
      makesIds,
      isTopRated,
      topRatedRank,
      account,
      accountEmail,
      accountPassword,
    } = this.state;

    return (
      <form className="mform" onSubmit={this.handleValidSubmit}>
        <Row>
          <Col lg="6">
            <FormSection title={strings.information} icon="nc-icon nc-bank">
              <FormGrid>
                <TextField
                  label={strings.name}
                  value={name}
                  onChange={(value) => this.setState({ name: value })}
                  error={errors.name}
                  placeholder={strings.garage}
                />
                <TextField
                  label={strings.phone}
                  value={phoneNumber}
                  onChange={(value) => this.setState({ phoneNumber: value })}
                  error={errors.phoneNumber}
                  help={strings.phone_unique_hint}
                />
              </FormGrid>
              <FormGrid full>
                <TextField
                  label={strings.address}
                  required
                  value={address}
                  onChange={(value) => this.setState({ address: value })}
                  error={errors.address}
                  help={strings.garage_address_hint}
                />
                <SelectField
                  label={strings.vehicle_makes}
                  required
                  isMulti
                  allowSelectAll
                  value={makesIds}
                  options={makes}
                  onChange={(value) => this.setState({ makesIds: value })}
                  error={errors.makesIds}
                />
              </FormGrid>

              {/* the rank belongs to the toggle, so it sits with it */}
              <div className="mform__inline">
                <SwitchField
                  label={strings.top_rated}
                  help={strings.top_rated_hint}
                  checked={isTopRated}
                  onChange={(checked) => this.setState({ isTopRated: checked })}
                />
                {isTopRated && (
                  <TextField
                    label={strings.top_rated_rank}
                    type="number"
                    min="0"
                    value={topRatedRank}
                    onChange={(value) => this.setState({ topRatedRank: value })}
                    help={strings.top_rated_rank_hint}
                  />
                )}
              </div>
            </FormSection>

            <FormSection title={strings.photos} icon="nc-icon nc-album-2">
              {this.renderPhotos()}
              {!id && (
                <span className="mfield__help">
                  {strings.photos_after_save_hint}
                </span>
              )}
            </FormSection>
          </Col>

          <Col lg="6">
            <FormSection title={strings.location} icon="nc-icon nc-pin-3">
              <Field required error={errors.location}>
                <LocationPicker
                  latitude={latitude}
                  longitude={longitude}
                  onChange={this.handleLocationChange}
                  onAddressResolved={this.handleAddressResolved}
                  height={190}
                />
              </Field>

              <FormGrid>
                <TextField
                  label={strings.latitude}
                  type="number"
                  step="any"
                  value={latitude}
                  onChange={(value) => this.setState({ latitude: value })}
                />
                <TextField
                  label={strings.longitude}
                  type="number"
                  step="any"
                  value={longitude}
                  onChange={(value) => this.setState({ longitude: value })}
                />
              </FormGrid>
            </FormSection>

            <FormSection title={strings.garage_login} icon="nc-icon nc-key-25">
              <FormGrid>
                <TextField
                  label={strings.email}
                  required={!id}
                  value={accountEmail}
                  onChange={(value) => this.setState({ accountEmail: value })}
                  error={errors.accountEmail}
                  help={strings.garage_login_hint}
                />
                <PasswordField
                  label={account ? strings.new_password : strings.password}
                  required={!account}
                  value={accountPassword}
                  onChange={(value) => this.setState({ accountPassword: value })}
                  error={errors.accountPassword}
                  help={account ? strings.password_change_hint : strings.password_hint}
                />
              </FormGrid>
            </FormSection>

          </Col>
        </Row>

        <FormAlert>{error}</FormAlert>
        <FormActions onCancel={this.props.onCancel} loading={loading} />
      </form>
    );
  }
}

